import { LowerCasePipe } from '@angular/common';
import { Component, computed, ElementRef, inject, signal, viewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ProductPlacementComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/product-placement/product-placement.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { FindingCardGridComponent } from '@cta-analytics-dialog/deliverable-analytics-content/finding-cards/finding-card-grid/finding-card-grid.component';
import { FindingCardMenuComponent } from '@cta-analytics-dialog/deliverable-analytics-content/finding-cards/finding-card-menu/finding-card-menu.component';
import { summarizeFindings } from '@cta-analytics-dialog/deliverable-analytics-content/finding-cards/finding-card.util';
import { FindingCardsState } from '@cta-analytics-dialog/deliverable-analytics-content/finding-cards/finding-cards.state';
import {
  IFindingSpan,
  IFindingSpans,
} from '@cta-analytics-dialog/deliverable-analytics-content/graphs/graph-finding-spans.interface';
import { TimelineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/timeline-graph/timeline-graph.component';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { IProductPlacementAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DurationUtilitiesService } from '@utilities/duration-utilities.service';
import { GraphUtilitiesService } from '@utilities/graph-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MultiMediaPostAnalyticsBaseDirective } from '../multi-media-post-analytics-base/multi-media-post-analytics-base.directive';
import { PostMediaViewerComponent } from '../post-media-viewer/post-media-viewer.component';
import { IProductPlacementPostAnalyticsTableData } from './product-placement-post-analytics.interface';

@Component({
  imports: [
    TranslateModule,
    TimelineGraphComponent,
    PostMediaViewerComponent,
    ProductPlacementComponent,
    LowerCasePipe,
    FindingCardGridComponent,
    FindingCardMenuComponent,
    NgxSkeletonLoaderModule,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-product-placement-post-analytics',
  templateUrl: './product-placement-post-analytics.component.html',
})
export class ProductPlacementPostAnalyticsComponent extends MultiMediaPostAnalyticsBaseDirective<
  IProductPlacementPostAnalyticsTableData,
  IProductPlacementAnalytics
> {
  public override readonly deliverableType = EDeliverableType.productPlacement;
  public override readonly displayedColumns: string[] = [];
  public override readonly dataSource: MatTableDataSource<IProductPlacementPostAnalyticsTableData> =
    new MatTableDataSource();

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.productPlacements?.find((p) => p.deliverableId === deliverableId);
    }

    return null;
  });

  public readonly currentMedia = computed(() => {
    const { src } = this.videoConfiguration();
    const mediaList = this.media();
    let found = mediaList.find((m) => m.url === src);

    if (!found && mediaList.length > 0) {
      [found] = mediaList;
    }

    return found;
  });

  public override readonly kpiCardData = null;

  public readonly findingCardGrid = viewChild(FindingCardGridComponent);
  public readonly mediaViewer = viewChild<ElementRef<HTMLElement>>('mediaViewer');
  public readonly findingCards = new FindingCardsState<IProductPlacementPostAnalyticsTableData>({
    isPendingDeletion: (card): boolean => this.hasRowPendingDeletions(card),
    loading: (): boolean => !this.findingsLoaded() || this.dataCount() === null,
    manualDeliverableStatusConfig: this.manualDeliverableStatusConfig,
    onJump: (cardId): void => this.onDataPointClick(cardId),
    removedIds: (): ReadonlySet<string> => this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>(),
    status: (): EAnalyticsStatus | null => this.currentDeliverable()?.status ?? null,
  });
  public readonly findingSpans = signal<IFindingSpans>({
    items: [],
    legend: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.graph.productVisibleLegend'),
    status: EAnalyticsStatus.ok,
  });
  public readonly findingsSummary = computed(() =>
    summarizeFindings(this.findingCards.activeSortedCards(), this.findingSpans().items, 1),
  );
  // Same status color as the graph's spans, for the card number badges.
  public readonly findingColor = computed(() =>
    this.graphUtilitiesService.getColorByStatus(this.findingSpans().status),
  );

  private readonly graphUtilitiesService = inject(GraphUtilitiesService);

  constructor() {
    super();

    this.chartConfiguration.set({
      title: { text: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.graph.findingsOverTime') },
    });
  }

  public override onDataPointClick(findingId: string): void {
    super.onDataPointClick(findingId);
    this.findingCards.highlightedId.set(findingId);
    this.findingCardGrid()?.scrollToCard(findingId);
  }

  // The in-page viewer sits below the grid, so a seek would otherwise happen out of sight.
  public override onRowClick(event: Event, element: IProductPlacementPostAnalyticsTableData): void {
    super.onRowClick(event, element);

    if (!this.usesExternalVideoLink()) {
      this.mediaViewer()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  protected override setCounts(): void {
    return;
  }

  protected override setKeywordList(): void {
    return;
  }

  protected override getKeywords(): string[] {
    return [];
  }

  protected override prepareChartAndTableData(): void {
    const tableData: IProductPlacementPostAnalyticsTableData[] = [];
    const spanRanges: Omit<IFindingSpan, 'number'>[] = [];
    const currentProductPlacementAnalytics = this.currentDeliverable();
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const findings = (currentProductPlacementAnalytics?.findings || [])
      .filter((f) => !removedIds.has(f.id))
      .sort((a, b) => (a.mediaIndex ?? 0) - (b.mediaIndex ?? 0));
    let imageFindingTimeOffset = 0;
    let previousMediaIndex = 0;
    let previousMediaIsVideo = this.media()[previousMediaIndex]?.isVideo ?? false;

    findings.forEach((finding) => {
      const { id } = finding;
      const isImage = !this.media()[finding.mediaIndex ?? 0]?.isVideo;
      const videoTime = finding.videoTime ?? 0;
      const mediaIndex = finding.mediaIndex ?? 0;
      const aggregatedVideoTime = this.calculateAggregatedVideoTime(mediaIndex, videoTime);
      const mediaIndexChanged = mediaIndex !== previousMediaIndex;

      if (mediaIndexChanged && previousMediaIsVideo) {
        imageFindingTimeOffset = 0;
        previousMediaIndex = mediaIndex;
      }

      previousMediaIsVideo = !isImage;

      const xAxisValue = isImage ? imageFindingTimeOffset++ : aggregatedVideoTime;
      const mediaDuration = this.mediaDurationsSeconds()[mediaIndex];
      const endVideoTime = videoTime + (finding.duration ?? 0);
      // Capped at the media's end so the span doesn't run into the next media on the aggregated axis.
      const endXAxisValue = isImage
        ? xAxisValue
        : this.calculateAggregatedVideoTime(
            mediaIndex,
            mediaDuration ? Math.min(endVideoTime, mediaDuration) : endVideoTime,
          );

      spanRanges.push({ end: endXAxisValue, id, start: xAxisValue });

      tableData.push({
        analyticsImage: finding.analyticsImage,
        duration: finding.duration,
        findingParentId: finding.parentId,
        id,
        mediaIndex,
        number: 0,
        sortTime: aggregatedVideoTime,
        videoTime,
        videoTimeLabel: DurationUtilitiesService.convertSecondsToVideoTime(aggregatedVideoTime, true),
      });
    });

    const cards = this.findingCards.setCards(tableData);
    const numberById = new Map(cards.map((card) => [card.id, card.number]));

    this.dataSource.data = cards;
    this.dataCount.set(cards.length);
    this.findingSpans.update((findingSpans) => ({
      ...findingSpans,
      items: spanRanges.map((range) => ({ ...range, number: numberById.get(range.id) ?? 0 })),
      status: currentProductPlacementAnalytics?.status ?? EAnalyticsStatus.ok,
    }));
  }
}
